import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { RangeField } from "@/components/kit";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { logError } from "@/lib/log";
import { HelpRating } from "@/components/playbook/HelpRating";

const StressStep = ({ title, value, onChange, cta, onNext, busy, testId }) => (
  <div className="mt-2 mm-fade-up" data-testid={`coping-step-${testId}`}>
    <p className="font-semibold text-lg mb-4">{title}</p>
    <RangeField label="Stress level" value={value} onChange={onChange} testId={`coping-stress-${testId}`} left="Calm" right="Very stressed" />
    <button className="mm-btn w-full" onClick={onNext} disabled={busy} data-testid={`coping-${testId}-next`}>{busy ? <Loader2 size={16} className="animate-spin" /> : cta}</button>
  </div>
);

const Steps = ({ skill, onDone }) => (
  <div className="mm-fade-up" data-testid="coping-step-doing">
    {skill.duration && <div className="text-xs font-semibold text-mm-primary uppercase tracking-wider">Takes about {skill.duration}</div>}
    {skill.steps?.length > 0 ? (
      <ol className="mt-3 space-y-2.5" data-testid="coping-steps-list">
        {skill.steps.map((s, i) => (
          <li key={s} className="flex gap-3 text-sm"><span className="w-6 h-6 rounded-full mm-gradient text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span><span className="pt-0.5">{s}</span></li>
        ))}
      </ol>
    ) : <p className="text-sm mt-3">{skill.description}</p>}
    {skill.tips?.length > 0 && (
      <div className="mm-soft p-3 mt-4 text-sm" data-testid="coping-tips"><span className="font-semibold">Tips: </span>{skill.tips.join(" · ")}</div>
    )}
    <button className="mm-btn w-full mt-5" onClick={onDone} data-testid="coping-done-button">I'm done</button>
  </div>
);

const Result = ({ skill, before, after, onClose }) => {
  const diff = before - after;
  const msg = diff > 0 ? `Stress dropped ${before} → ${after} 🎉` : diff === 0 ? `Stress held steady at ${after}` : `Stress went ${before} → ${after}`;
  const sub = diff > 0 ? "Nice work — this skill helped. It's saved to your insights." : "That's okay — not every skill fits every moment. Try another one.";
  return (
    <div className="text-center py-4 mm-fade-up" data-testid="coping-step-result">
      <div className="text-5xl">{diff > 0 ? "🌿" : "💜"}</div>
      <p className="font-display text-2xl font-bold mt-3" data-testid="coping-result-text">{msg}</p>
      <p className="text-sm text-mm-muted mt-2">{sub}</p>
      {!skill.custom && <HelpRating kind="skill" toolId={skill.id} toolName={skill.name} className="justify-center mt-5" />}
      <button className="mm-btn w-full mt-6" onClick={onClose} data-testid="coping-result-close">Close</button>
    </div>
  );
};

export const SkillSession = ({ skill, onClose }) => {
  const [stage, setStage] = useState("before");
  const [before, setBefore] = useState(5);
  const [after, setAfter] = useState(5);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/skill-logs", { skill_id: skill.id, skill_name: skill.name, category: skill.category, stress_before: before, stress_after: after });
      if (!skill.custom) api.post(`/coping-skills/${skill.id}/use`).catch((e) => logError("Usage count failed:", e));
      setStage("result");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="coping-log-dialog">
        <DialogHeader><DialogTitle>{skill.icon} {skill.name}</DialogTitle><DialogDescription>{skill.description}</DialogDescription></DialogHeader>
        {stage === "before" && <StressStep title="How stressed are you right now?" value={before} onChange={setBefore} cta="Start skill" onNext={() => { setAfter(before); setStage("doing"); }} testId="before" />}
        {stage === "doing" && <Steps skill={skill} onDone={() => setStage("after")} />}
        {stage === "after" && <StressStep title="How stressed are you now?" value={after} onChange={setAfter} cta="Save" onNext={save} busy={busy} testId="after" />}
        {stage === "result" && <Result skill={skill} before={before} after={after} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
};
